// EG cart drawer controller: open/close (trigger click, backdrop, Esc) and
// auto-open on add-to-cart events.
//
// Content refresh is deliberately NOT here. The drawer body is a real core
// <cart-items-component data-drawer> (snippets/eg-cart-drawer.liquid), so core
// component-cart-items.js already listens for CartLinesUpdateEvent and morphs
// the drawer's data-hydration-key region from the event's sections payload —
// falling back to a Section Rendering API re-fetch of the eg-header section
// when no payload is present. Duplicating that here would double-render the
// same region (docs/discovery/r2-cart-extensibility.md §4: one fetch, one
// event, ONE morph call site).
//
// Auto-open mirrors core cart-drawer.js #handleCartLinesUpdate: react to
// action 'add', wait for the event promise, skip when the add came from an
// open modal dialog (e.g. the Workshop wizard, which owns its own navigation
// to /cart) and when the add errored.
import { StandardEvents } from '@shopify/events';

class EgCartDrawer {
  constructor() {
    document.addEventListener('click', this.onClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    // Covers every close path (close button, backdrop, native Esc cancel).
    this.dialog?.addEventListener('close', this.onDialogClose);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    const el = document.getElementById('eg-cart-drawer');
    return el instanceof HTMLDialogElement ? el : null;
  }

  onClick = (e) => {
    const dialog = this.dialog;
    if (!dialog || !(e.target instanceof Element)) return;
    if (e.target.closest('[data-eg-cart-open]')) {
      this.open();
      return;
    }
    if (dialog.open && e.target.closest('[data-eg-cart-close]')) {
      dialog.close();
      return;
    }
    // Backdrop: the dialog element itself is the click target only when the
    // click lands outside its laid-out children.
    if (dialog.open && e.target === dialog) dialog.close();
  };

  open() {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    this.#reflectExpanded(true);
  }

  onDialogClose = () => {
    this.#reflectExpanded(false);
  };

  #reflectExpanded(isOpen) {
    document
      .querySelectorAll('[data-eg-cart-open]')
      .forEach((btn) => btn.setAttribute('aria-expanded', String(isOpen)));
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  onCartLinesUpdate = (event) => {
    const dialog = this.dialog;
    if (!dialog || !dialog.hasAttribute('data-eg-auto-open')) return;
    if (event.action !== 'add' || dialog.open) return;

    const sourceModal =
      event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        // An add dispatched from inside an open modal (Workshop wizard)
        // navigates to /cart itself — opening over it would fight that flow.
        if (sourceModal?.open) return;
        this.open();
      })
      .catch(() => {
        /* rejected adds never open the drawer */
      });
  };
}

if (!window.__egCartDrawer) window.__egCartDrawer = new EgCartDrawer();
